import { useState, useEffect, useRef, useMemo } from "react";
import { Eye, EyeOff, ChevronDown, X } from "react-feather";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { authService } from "../../../services/auth.service";
import { AxiosError } from "axios";
import { nicheService } from "../../../services/niche.service";
import { useAuth } from "../../../hooks/useAuth";
import logo from "../../../assets/logo.svg";
import { countries } from "../../../constants/countries";
import { Checkbox } from "@/components/ui/checkbox";

import { tourManager } from "../../../utils/tourManager";
import { readReturnUrl, withReturnUrl } from "@/utils/returnUrl";
import { beginGoogleSignIn, GoogleSignInErrorState } from "@/lib/googleSignIn";
import { useContext } from "react";
import { usePortalBranding, HostPortalContext } from "@/contexts/portalBranding";
import { useBrandTheme } from "@/theme/useBrandTheme";
import { portalBrandingService } from "@/services/portalBranding.service";

interface Niche {
  id: string;
  name: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  nicheId: string | null;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  niches: string[];
    agreeToTerms: boolean;
  
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  agreeToTerms?: string;
}

export function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isNicheOpen, setIsNicheOpen] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [availableNiches, setAvailableNiches] = useState<Niche[]>([]);
  const [isLoadingNiches, setIsLoadingNiches] = useState(true);
  const [returnUrl, setReturnUrl] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
    agreeToTerms: false,
  });

  // Check if coming from Google OAuth
  const locationState = location.state as { googleData?: {
    googleId: string;
    email: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
  }} | null;
  
  const googleData = locationState?.googleData;
  // Set when GoogleComplete sends a new Google account here to finish signup.
  const isGoogleSignup = !!googleData;

  // Check for completion token in query params
  const [completionToken, setCompletionToken] = useState<string | null>(null);
  const [isCompletionFlow, setIsCompletionFlow] = useState(false);
  const [isLoadingProfileInfo, setIsLoadingProfileInfo] = useState(false);


  const [formData, setFormData] = useState<FormData>({
    firstName: googleData?.firstName || "",
    lastName: googleData?.lastName || "",
    email: googleData?.email || "",
    password: "",
    confirmPassword: "",
    country: "US",
    niches: [],
    agreeToTerms: false,
  });

  const { login } = useAuth();

  // Google sign-in leaves the page: a redirect through the backend, which is
  // what lets it work from every white-label portal host (see lib/googleSignIn).
  // The outcome arrives on /affiliate/auth/google/complete and, for a new
  // account, comes back here with `googleData` in the location state.
  // Deliberately no loading state: if the user presses Back on Google's
  // screen, the browser may restore this page as-is, and a stuck spinner
  // would leave every button disabled.
  const handleGoogleLogin = () => {
    setError("");
    beginGoogleSignIn({
      from: `${location.pathname}${location.search}`,
      returnUrl: returnUrl || undefined,
    });
  };

  // A failed Google sign-in comes back here with the reason in the state.
  useEffect(() => {
    const state = location.state as GoogleSignInErrorState | null;
    if (state?.googleError) {
      setError(state.googleError);
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [location, navigate]);

  // State for brand join flow
  const [pendingJoinBrandId, setPendingJoinBrandId] = useState<string | null>(
    null
  );
  const [pendingJoinBrandName, setPendingJoinBrandName] = useState<
    string | null
  >(null);
  const [pendingJoinBrandUsername, setPendingJoinBrandUsername] = useState<
    string | null
  >(null);
  const [shouldAutoJoin, setShouldAutoJoin] = useState(false);
  const portalBranding = usePortalBranding();
  // Host-derived, unlike `portalBranding`, which also reflects an authenticated
  // affiliate's scope. Signup has no session yet, so the hostname is the only
  // source of "which brand's portal is this".
  const { branding: hostPortal } = useContext(HostPortalContext);

  // Branding for a signup that arrived through a brand's join link on the
  // marketplace domain (ticket 5): when that brand's white-label portal is
  // live, signup wears the brand's identity even though the hostname is
  // Reveshare's. Null for ordinary brands — the backend answers null unless
  // the portal is actually live.
  const [joinBranding, setJoinBranding] = useState<{
    portalName: string;
    portalLogo: string | null;
    primaryColour: string | null;
    secondaryColour: string | null;
  } | null>(null);

  useEffect(() => {
    if (!pendingJoinBrandId || portalBranding.isWhiteLabel) return;
    let cancelled = false;
    portalBrandingService
      .getPublicConfigForBrand(pendingJoinBrandId)
      .then((config) => {
        if (!cancelled && config) {
          setJoinBranding({
            // The brand's own name — there is no separate portal name.
            portalName: config.brandName ?? "",
            portalLogo: config.portalLogo,
            primaryColour: config.primaryColour ?? null,
            secondaryColour: config.secondaryColour ?? null,
          });
        }
      })
      .catch(() => {
        // Purely cosmetic — the scoping decision is the backend's.
      });
    return () => {
      cancelled = true;
    };
  }, [pendingJoinBrandId, portalBranding.isWhiteLabel]);

  // A join-link signup on the marketplace wears the brand's colours too, and
  // hands them back on leaving. On a branded host this is a no-op — the
  // host-level PortalBrandingTheme already owns the theme.
  useBrandTheme(
    joinBranding
      ? {
          primayColour: joinBranding.primaryColour,
          secondaryColour: joinBranding.secondaryColour,
        }
      : null,
    true
  );

  // What the backend should record as the affiliate's home brand: the branded
  // portal we're on, or the brand whose join link brought us here. Safe to
  // send unconditionally — the backend drops it unless white-label is live.
  const homeBrandDetailId =
    portalBranding.homeBrandDetailId ?? pendingJoinBrandId ?? undefined;

  // Extract returnUrl and brand data from query parameters or sessionStorage
  useEffect(() => {
    // Get return URL from query params
    const params = new URLSearchParams(location.search);
    const returnUrlParam = params.get("returnUrl");
    const autoJoinParam = params.get("autoJoin");
    const tokenParam = params.get("token");

    // Check for completion token
    if (tokenParam) {
      setCompletionToken(tokenParam);
      setIsCompletionFlow(true);
      setIsLoadingProfileInfo(true);
      
      // Fetch profile info from backend
      authService.getCompleteProfileInfo(tokenParam)
        .then((profileInfo) => {
          // Prefill form with fetched data
          setFormData(prev => ({
            ...prev,
            firstName: profileInfo.firstName,
            lastName: profileInfo.lastName,
            email: profileInfo.email,
          }));

          setIsLoadingProfileInfo(false);
        })
        .catch((error) => {
          console.error("Failed to fetch profile info:", error);
          setError(
            error.response?.data?.message || 
            "Invalid or expired completion link. Please request a new invite."
          );
          setIsLoadingProfileInfo(false);
        });
    }

    if (returnUrlParam) {
      // readReturnUrl rejects off-origin values, so a crafted
      // `?returnUrl=https://evil.com` can't turn signup into a redirector.
      setReturnUrl(readReturnUrl(location.search) || "");
    } else {
      // Check if we have a stored return URL
      const storedReturnUrl = sessionStorage.getItem("brandJoinReturnUrl");
      if (storedReturnUrl) {
        setReturnUrl(storedReturnUrl);
      }
    }

    // Check if we should auto-join a brand after signup
    if (autoJoinParam === "true") {
      setShouldAutoJoin(true);
    }

    // Check for pending brand join data
    const brandId = sessionStorage.getItem("pendingJoinBrandId");
    const brandName = sessionStorage.getItem("pendingJoinBrandName");
    const brandUsername = sessionStorage.getItem("pendingJoinBrandUsername");
    

    if (brandId) setPendingJoinBrandId(brandId);
    if (brandName) setPendingJoinBrandName(brandName);
    if (brandUsername) setPendingJoinBrandUsername(brandUsername);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsNicheOpen(false);
      }

      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  useEffect(() => {
    const fetchNiches = async () => {
      try {
        const niches = await nicheService.getAvailableNiches('enabled');
        setAvailableNiches(niches);
      } catch (error) {
        console.error("Failed to fetch niches:", error);
        setError("Failed to load available niches");
      } finally {
        setIsLoadingNiches(false);
      }
    };

    fetchNiches();
  }, []);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setError("");
    setIsLoading(true);

    try {
      console.log("Submitting signup data:", {
        ...formData,
        password: "[REDACTED]",
        confirmPassword: "[REDACTED]",
      });

      // Use unified signup endpoint for both regular and Google signup
      const response = await authService.signUp({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: isGoogleSignup ? undefined : formData.password,
        niches: formData.niches,
        country: formData.country,
        ...(isGoogleSignup && googleData && {
          googleId: googleData.googleId,
          profileImage: googleData.profileImage,
        }),
        ...(isCompletionFlow && completionToken && {
          completionToken: completionToken,
        }),
        ...(homeBrandDetailId && { homeBrandDetailId }),
      });

      console.log("Signup response received:", response);

      if (response && response.token && response.user) {
        console.log("Logging in user with response:", {
          user: response.user,
          token: response.token ? "[TOKEN EXISTS]" : "[NO TOKEN]",
          refreshToken: response.refreshToken
            ? "[REFRESH TOKEN EXISTS]"
            : "[NO REFRESH TOKEN]",
        });

        login(response);
        console.log("User logged in successfully");

        // Check if user is logging in within first 24 hours of signup
        const isWithin24Hours =
          new Date().getTime() -
            new Date(
              response.user.createdAt || new Date().toISOString()
            ).getTime() <
          24 * 60 * 60 * 1000;

        // Check if createdAt is not 14 days old (tour should still be available)
        const isWithin14Days =
          new Date().getTime() -
            new Date(
              response.user.createdAt || new Date().toISOString()
            ).getTime() <
          14 * 24 * 60 * 60 * 1000;

        // Get existing tour data
        const tourData = tourManager.getTourData();

        // Initialize tour data if:
        // 1. No existing tour data AND user is within 24 hours of signup
        // 2. OR user is within 14 days and no tour data exists
        if ((!tourData && isWithin24Hours) || (!tourData && isWithin14Days)) {
          tourManager.initializeTourData();
          console.log("Tour data initialized for new user");
        } else {
          console.log("Tour data already exists or user outside time window");
        }

        // Clean up session storage
        const brandUsername = pendingJoinBrandUsername;
        const autoJoin = shouldAutoJoin;

        // Clear session storage data
        sessionStorage.removeItem("brandJoinReturnUrl");
        sessionStorage.removeItem("pendingJoinBrandId");
        sessionStorage.removeItem("pendingJoinBrandName");
        sessionStorage.removeItem("pendingJoinBrandUsername");

        // Determine destination URL
        let destination = "/affiliate/dashboard";
        if (returnUrl) {
          destination = returnUrl;

          // If we should auto-join and have a brand username, add a query parameter
          if (autoJoin && brandUsername) {
            destination +=
              (destination.includes("?") ? "&" : "?") + "autoJoin=true";
          }
        } else if (hostPortal?.brandUsername) {
          // Signed up directly on a brand's portal — no join link, so no
          // program was named. The generic dashboard is the wrong landing:
          // there is exactly one brand here, and the affiliate has not joined
          // it yet. Send them to that brand's page, where the join call to
          // action is.
          //
          // The program-specific case needs nothing here: a `?p=<token>` link
          // already rides through `returnUrl` above.
          destination = `/affiliate/brands/${hostPortal.brandUsername}`;
        }

        console.log("Navigating to:", destination);

        // The Reveshare Discord invite is a cross-brand surface — never queue
        // it for an affiliate who signed up through a white-label portal.
        if (destination === "/affiliate/dashboard" && !homeBrandDetailId) {
          sessionStorage.setItem("affiliateShowDiscordModal", "true");
        }

        setTimeout(() => {
          navigate(destination);
        }, 100);
      } else {
        setError("An error occurred during signup");
      }
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      setError(
        error.response?.data?.message || "An error occurred during signup"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: ValidationErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Only validate password if not Google signup
    if (!isGoogleSignup) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms and conditions";
    }

    setValidationErrors(newErrors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      password: true,
      confirmPassword: true,
      agreeToTerms: true,
    });

    return Object.keys(newErrors).length === 0;
  };

  // Handle niche selection with max 3 niches
  const handleNicheChange = (nicheId: string) => {
    if (formData.niches.includes(nicheId)) {
      // Remove the niche if already selected
      setFormData((prev) => ({
        ...prev,
        niches: prev.niches.filter((id) => id !== nicheId),
      }));
    } else {
      // Add the niche if not already selected and less than 3 niches are selected
      if (formData.niches.length < 3) {
        setFormData((prev) => ({
          ...prev,
          niches: [...prev.niches, nicheId],
        }));
      }
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form first
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    setError("");
    setIsLoading(true);
    handleSubmit();
  };

  // Helper function to validate a specific field
  const validateField = (
    field: string,
    value: string | boolean
  ): ValidationErrors => {
    const errors: ValidationErrors = {};
    switch (field) {
      case "firstName":
        if (!value) errors.firstName = "First name is required";
        break;
      case "lastName":
        if (!value) errors.lastName = "Last name is required";
        break;
      case "email":
        if (!value) errors.email = "Email is required";
        else if (typeof value === "string" && !/\S+@\S+\.\S+/.test(value)) {
          errors.email = "Email is invalid";
        }
        break;
      case "password":
        if (!value) errors.password = "Password is required";
        else if (typeof value === "string" && value.length < 8) {
          errors.password = "Password must be at least 8 characters";
        }
        break;
      case "confirmPassword":
        if (!value) errors.confirmPassword = "Repeat password is required";
        else if (typeof value === "string" && value !== formData.password) {
          errors.confirmPassword = "Passwords do not match";
        }
        break;
      case "agreeToTerms":
        if (value !== true)
          errors.agreeToTerms = "You must agree to the terms and conditions";
        break;
      default:
        break;
    }
    return errors;
  };

  // Handle input field changes and validate them
  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (touched[field as keyof typeof touched]) {
      const fieldErrors = validateField(field, value);
      setValidationErrors((prev) => {
        const next = { ...prev } as ValidationErrors;
        // Clear previous error for this field first
        (next as any)[field] = undefined;
        // Re-apply if there is still an error
        return { ...next, ...fieldErrors };
      });
    }
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const renderFieldError = (field: string) => {
    if (
      touched[field as keyof typeof touched] &&
      validationErrors[field as keyof ValidationErrors]
    ) {
      return (
        <p className="text-red-500 text-xs mt-1">
          {validationErrors[field as keyof ValidationErrors]}
        </p>
      );
    }
    return null;
  };

  // This function has been moved and merged with the one above

  const brandJoinMessage = useMemo(() => {
    if (pendingJoinBrandName) {
      return `You are signing up to join ${pendingJoinBrandName}'s affiliate program.`;
    }
    return "";
  }, [pendingJoinBrandName]);

  return (
    <div className="lg:w-1/3 md:m-auto pt-4 flex items-center md:h-full mx-auto md:pt-0 ">
      <div className="mx-auto w-[98%] h-full py-2 md:h-[90%] md:m-auto flex items-center justify-center bg-card border border-border md:rounded-[24px] rounded-[12px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]  ">
        <div className="overflow-y-auto w-full pt-7  px-5 h-full">
          <div className="flex justify-center mb-8">
            {(() => {
              const branding = portalBranding.isWhiteLabel
                ? {
                    name: portalBranding.portalName,
                    logo: portalBranding.portalLogo,
                  }
                : joinBranding
                ? { name: joinBranding.portalName, logo: joinBranding.portalLogo }
                : null;
              if (branding) {
                return branding.logo ? (
                  <img
                    src={branding.logo}
                    alt={branding.name}
                    className="h-12 w-12 rounded object-contain"
                  />
                ) : (
                  <span className="text-xl font-semibold">{branding.name}</span>
                );
              }
              return portalBranding.isReady ? (
                <img src={logo} alt="Logo" className="h-12 w-12" />
              ) : null;
            })()}
          </div>
          <div className="max-w-md w-full mx-auto space-y-4 pb-7">
            <h1 className="mt-6 font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
              Lets Get started
            </h1>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg">
                {error}
              </div>
            )}
            {brandJoinMessage && (
              <div className="mb-4 p-3 bg-brand-50 border border-brand-100 text-brand-800 rounded-lg">
                {brandJoinMessage}
              </div>
            )}
            {isCompletionFlow && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-100 text-blue-800 rounded-lg">
                <p className="font-medium mb-1">Complete your affiliate profile</p>
                <p className="text-sm">
                  Your email and name have been pre-filled. Complete the form below to activate your account and start earning commissions.
                </p>
              </div>
            )}
            {isLoadingProfileInfo && (
              <div className="mb-4 p-3 bg-muted border border-border text-muted-foreground rounded-lg">
                <p className="text-sm">Loading your profile information...</p>
              </div>
            )}
            <p className="text-muted-foreground mb-8 text-center">
              Tell us some details about you
            </p>

            <form onSubmit={(e) => onSubmit(e)} className="space-y-6">
              {isLoadingProfileInfo && (
                <div className="absolute inset-0 bg-card bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="text-center">
                    <div className="w-8 h-8 border-t-2 border-brand-600 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm text-muted-foreground">Loading your profile...</p>
                  </div>
                </div>
              )}
              {/* First Name */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="firstname"
                    className="block text-sm font-medium text-muted-foreground mb-1"
                  >
                    First name
                  </label>
                  <input
                    id="firstname"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    onBlur={() => handleBlur("firstName")}
                    placeholder="First name"
                    className={`w-full px-3 py-2 border ${
                      touched.firstName && validationErrors.firstName
                        ? "border-red-300"
                        : "border-border"
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500`}
                  />
                  {renderFieldError("firstName")}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    onBlur={() => handleBlur("lastName")}
                    placeholder="Last name"
                    className={`w-full px-3 py-2 border ${
                      touched.lastName && validationErrors.lastName
                        ? "border-red-300"
                        : "border-border"
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500`}
                  />
                  {renderFieldError("lastName")}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Email {isGoogleSignup && <span className="text-xs text-muted-foreground">(from Google)</span>}
                  {isCompletionFlow && <span className="text-xs text-muted-foreground">(pre-filled from invite)</span>}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  onBlur={() => handleBlur("email")}
                  placeholder="olivia@untitledui.com"
                  readOnly={isGoogleSignup || isCompletionFlow}
                  disabled={isGoogleSignup || isCompletionFlow}
                  className={`w-full px-3 py-2 border ${
                    touched.email && validationErrors.email
                      ? "border-red-300"
                      : "border-border"
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                    isGoogleSignup || isCompletionFlow ? "bg-muted cursor-not-allowed" : ""
                  }`}
                />
                {renderFieldError("email")}
              </div>

              {/* Niche Selection */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Niche{" "}
                  <span className="text-xs text-muted-foreground">
                    (optional)
                  </span>
                </label>
                <div className="relative" ref={dropdownRef}>
                  <div
                    className="flex flex-wrap gap-2 p-3 border border-border rounded-lg min-h-[44px] cursor-pointer"
                    onClick={() => setIsNicheOpen(!isNicheOpen)}
                  >
                    {formData.niches.map((nicheId) => {
                      const niche = availableNiches.find(
                        (n) => n.id === nicheId
                      );
                      return niche ? (
                        <span
                          key={`selected-${nicheId}`}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-brand-50 text-brand-700 rounded-md text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {niche.name}
                          <button
                            onClick={() => handleNicheChange(nicheId)}
                            className="hover:text-brand-900"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </span>
                      ) : null;
                    })}
                    {formData.niches.length === 0 && (
                      <span className="text-muted-foreground">Select categories</span>
                    )}
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground ml-auto transition-transform ${
                        isNicheOpen ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {/* Dropdown Menu */}
                  {isNicheOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg py-1 max-h-60 overflow-auto">
                      {isLoadingNiches ? (
                        <div className="px-3 py-2 text-muted-foreground">
                          Loading niches...
                        </div>
                      ) : (
                        availableNiches.map((niche) => (
                          <button
                            key={`option-${niche.id}`}
                            type="button"
                            onClick={() => handleNicheChange(niche.id)}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center justify-between ${
                              formData.niches.includes(niche.id)
                                ? "text-brand-700 bg-brand-50"
                                : "text-foreground"
                            }`}
                            disabled={
                              formData.niches.length >= 3 &&
                              !formData.niches.includes(niche.id)
                            }
                          >
                            {niche.name}
                            {formData.niches.includes(niche.id) && (
                              <span className="text-brand-600">✓</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Select up to 3 industries that describe your audience the best
                </p>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  Country
                </label>
                <div className="relative" ref={countryDropdownRef}>
                  <div
                    className="flex items-center justify-between w-full px-3 py-2 border border-border rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500"
                    onClick={() => setIsCountryOpen(!isCountryOpen)}
                  >
                    <span>
                      {countries.find((c) => c.code === formData.country)
                        ?.name || "Select country"}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform ${
                        isCountryOpen ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {isCountryOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg py-1 max-h-60 overflow-auto">
                      {countries.map((country) => (
                        <button
                          key={`country-${country.code}`}
                          type="button"
                          onClick={() => {
                            handleChange("country", country.code);
                            setIsCountryOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center justify-between ${
                            formData.country === country.code
                              ? "text-brand-700 bg-brand-50"
                              : "text-foreground"
                          }`}
                        >
                          {country.name}
                          {formData.country === country.code && (
                            <span className="text-brand-600">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Password - Only show for non-Google signups */}
              {!isGoogleSignup && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        onBlur={() => handleBlur("password")}
                        placeholder="Enter password"
                        className={`w-full px-3 py-2 border ${
                          touched.password && validationErrors.password
                            ? "border-red-300"
                            : "border-border"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {renderFieldError("password")}
                  </div>

                  {/* Repeat Password */}
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-muted-foreground"
                    >
                      Repeat Password
                    </label>
                    <div className="relative mt-1">
                      <input
                        id="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) =>
                          handleChange("confirmPassword", e.target.value)
                        }
                        onBlur={() => handleBlur("confirmPassword")}
                        placeholder="Repeat your password"
                        className={`block w-full px-3 py-2 border ${
                          touched.confirmPassword &&
                          validationErrors.confirmPassword
                            ? "border-red-300"
                            : "border-border"
                        } rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500`}
                      />
                    </div>
                    {renderFieldError("confirmPassword")}
                  </div>
                </>
              )}

              {/* Terms Agreement */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onCheckedChange={(checked) => {
                    const isChecked = Boolean(checked);
                    setFormData((prev) => ({
                      ...prev,
                      agreeToTerms: isChecked,
                    }));
                    // Clear error if user checks the box
                    setValidationErrors((prev) => ({
                      ...prev,
                      agreeToTerms: isChecked ? undefined : prev.agreeToTerms,
                    }));
                  }}
                />
                <label
                  htmlFor="agreeToTerms"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I agree to the{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    className="text-brand-600 hover:text-brand-500"
                  >
                    Terms and Conditions
                  </Link>
                </label>
              </div>
              {renderFieldError("agreeToTerms")}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={
                  isLoading ||
                  isLoadingProfileInfo ||
                  !formData.agreeToTerms ||
                  (!isGoogleSignup && formData.password !== formData.confirmPassword)
                }
                className={`w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${
                  !formData.agreeToTerms ||
                  isLoadingProfileInfo ||
                  (!isGoogleSignup && formData.password !== formData.confirmPassword)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-brand-600 hover:bg-brand-700"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50`}
              >
                {isLoading || isLoadingProfileInfo ? (
                  <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin mr-2" />
                ) : null}
                <span>
                  {isLoadingProfileInfo 
                    ? "Loading..." 
                    : isGoogleSignup || isCompletionFlow 
                      ? "Complete Sign Up" 
                      : "Sign Up"}
                </span>
              </button>

              {/* Google Sign-Up Button - Only show for non-Google signups and non-completion flows */}
              {!isGoogleSignup && !isCompletionFlow && (
                <>
                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-muted text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  {/* Google Button */}
                  <button
                    type="button"
                    onClick={() => handleGoogleLogin()}
                    disabled={isLoading}
                    className="w-full py-2 px-4 bg-card border border-border rounded-lg text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign up with Google
                  </button>
                </>
              )}

              {/* Login Link */}
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link
                  to={withReturnUrl("/auth/login", returnUrl)}
                  className="text-brand-600 hover:text-brand-700 font-medium"
                >
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
